import { observer } from "mobx-react-lite";
import { useEffect, useRef, useState } from "react";

import { Badge, BadgeSize, BadgeVariant } from "../../ui/Badge";
import { Button } from "../../ui/button";
import { useUnifiedRegistry } from "../../UnifiedRegistry/unified-registry-context";
import { useChooseEntityConfig } from "../choose-entity-config-context";
import { useFolderScope } from "../folder-scope-context-provider";

import ChooseEntityVariantView, { ChooseEntityVariantProvider } from "./ChooseEntityVariantView";
import { EntitySelection } from "./ChooseEntityVariantView/choose-entity-variant-context";
import { hasVersions, isSameEntityVariant } from "./utils";

import { getEntityNumericId, Entity, EntityType } from "@/queries";
import { getEntityName, getItemColor } from "@/components/UnifiedRegistry/utils";
import UnifiedRegistry from "@/components/UnifiedRegistry";
import { OverflowDisplayTooltip } from "@/components/ui/Tooltip/OverflowDisplayTooltip";
import { DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { EntityIcon } from "@/components/ui/icons";

interface ChooseEntityViewMultiProps<T extends EntityType = EntityType> {
  open: boolean;
  type: T;
  currentFolderId: number | null;
  readonly?: boolean;
  selectedEntities?: Entity[];
  min?: number;
  max?: number;
  onConfirm: (entities: Entity[]) => void;
  onCancel: () => void;
}

const ChooseEntityViewMulti = observer(
  <T extends EntityType = EntityType>({
    open,
    type,
    currentFolderId: propCurrentFolderId,
    readonly = false,
    selectedEntities: initialSelectedEntities = [],
    min,
    max,
    onConfirm,
    onCancel,
  }: ChooseEntityViewMultiProps<T>) => {
    const folderScope = useFolderScope();
    const [currentFolderId, setCurrentFolderId] = useState<number | null>(
      initialSelectedEntities.length > 0 ? propCurrentFolderId : folderScope,
    );

    const registry = useUnifiedRegistry();
    const config = useChooseEntityConfig();
    const allowDuplicateVariants = config ? config.allowDuplicateVariants : false;
    const previousOpenRef = useRef(open);

    // Reset folder location only when dialog opens (transitions from closed to open)
    useEffect(() => {
      if (open && !previousOpenRef.current) {
        const targetFolder = initialSelectedEntities.length > 0 ? propCurrentFolderId : folderScope;
        setCurrentFolderId(targetFolder);
      }
      previousOpenRef.current = open;
    }, [open, propCurrentFolderId, folderScope, initialSelectedEntities.length]);

    useEffect(() => {
      registry.selectEntities(initialSelectedEntities);
      return () => {
        registry.clearSelection();
      };
    }, [registry, initialSelectedEntities]);

    const canConfirm = () => {
      const count = registry.selectedCount;
      if (count === 0) {
        return false;
      }
      if (min !== undefined && count < min) {
        return false;
      }
      if (max !== undefined && count > max) {
        return false;
      }
      return true;
    };

    const handleConfirm = () => {
      if (canConfirm()) {
        onConfirm([...registry.selectedEntities]);
      }
    };

    const handleItemClick = (item: Entity) => {
      if (readonly) {
        return;
      }
      if (item.type === EntityType.FOLDER) {
        setCurrentFolderId(getEntityNumericId(item.id) ?? null);
        return;
      }

      // Check if this exact variant is selected
      const isCurrentlySelected = registry.isSelected(item);

      if (isCurrentlySelected) {
        if (min !== undefined && registry.selectedCount <= min) {
          return;
        }
        registry.deselectEntity(item);
      } else {
        if (max !== undefined && registry.selectedCount >= max) {
          return;
        }

        // For versioned entities without a variant selected, go to variant selection
        // Allow selecting same entity with different variants when enabled
        if (hasVersions(item) && allowDuplicateVariants) {
          registry.selectEntity({ ...item, _touched: false });
        } else {
          registry.selectEntity(item);
        }
      }
    };

    const getConstraintText = () => {
      const parts: string[] = [];
      if (min !== undefined && min > 0) {
        parts.push(`Min: ${min}`);
      }
      if (max !== undefined) {
        parts.push(`Max: ${max}`);
      }
      return parts.length > 0 ? `(${parts.join(", ")})` : "";
    };

    const getEntitySuffix = (entity: Entity) => {
      if (entity.label) {
        return ` (${entity.label})`;
      }
      if (entity.version_number) {
        return ` (v${entity.version_number})`;
      }
      if (entity._touched && allowDuplicateVariants) {
        return ` (All Versions)`;
      }
      return "";
    };

    const entityWithoutAssignedVersion = registry.selectedEntities.find(
      (e: Entity) => hasVersions(e) && !e._touched && e.metadata?.latest_version_number,
    );

    if (entityWithoutAssignedVersion && hasVersions(entityWithoutAssignedVersion)) {
      return (
        <ChooseEntityVariantProvider
          entity={entityWithoutAssignedVersion}
          readonly={readonly}
          multiSelect
          selectedEntities={registry.selectedEntities.filter(
            (e: Entity) => e !== entityWithoutAssignedVersion,
          )}
          onSelect={(selection: EntitySelection) => {
            // Update the entity with the selected version or label
            const updatedEntity =
              selection.type === "version"
                ? {
                    ...entityWithoutAssignedVersion,
                    version_number: selection.version,
                    _touched: true,
                  }
                : {
                    ...entityWithoutAssignedVersion,
                    version_number: undefined,
                    label: selection.label,
                    _touched: true,
                  };

            // If selecting "All Versions" (version_number === undefined with _touched)
            // remove all other variants of this entity
            const isAllVersions =
              allowDuplicateVariants &&
              selection.type === "version" &&
              selection.version === undefined;

            if (isAllVersions) {
              // Remove all existing variants of this entity
              const variantsToRemove = registry.selectedEntities.filter(
                (e: Entity) =>
                  e.id === entityWithoutAssignedVersion.id &&
                  e.type === entityWithoutAssignedVersion.type,
              );
              variantsToRemove.forEach(variant => registry.deselectEntity(variant));
              // Add the "All Versions" entity
              registry.selectEntity(updatedEntity);
            } else {
              // Check if this exact variant is already selected
              const isDuplicateVariant =
                allowDuplicateVariants &&
                registry.selectedEntities.some(
                  (e: Entity) =>
                    e !== entityWithoutAssignedVersion &&
                    isSameEntityVariant(e, updatedEntity, allowDuplicateVariants),
                );

              registry.deselectEntity(entityWithoutAssignedVersion);

              // Only add if not a duplicate variant (when duplicate variants are allowed)
              if (!isDuplicateVariant) {
                registry.selectEntity(updatedEntity);
              }
            }
          }}
          onOpenChange={(isOpenDialog: boolean) => {
            if (!isOpenDialog) {
              onCancel();
            }
          }}
          onBack={() => {
            registry.deselectEntity(entityWithoutAssignedVersion);
          }}
        >
          <ChooseEntityVariantView />
        </ChooseEntityVariantProvider>
      );
    }
    return (
      <>
        <DialogHeader className="border-b border-gray-200 px-6 py-4">
          <DialogTitle className="flex items-center gap-3 text-lg font-semibold text-gray-900">
            <div
              className={`flex h-10 w-full max-w-[40px] flex-shrink-0 items-center justify-center rounded-full ${getItemColor(
                type,
              )}`}
            >
              <EntityIcon type={type} size="md" />
            </div>
            <span className="min-w-0 flex-1">{`Select ${getEntityName(type)}s`}</span>
          </DialogTitle>
          <DialogDescription className="mt-3 space-y-3  pr-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700">
                  {registry.selectedCount > 0 ? (
                    <>
                      {registry.selectedCount} {registry.selectedCount === 1 ? "item" : "items"}{" "}
                      selected
                    </>
                  ) : (
                    "No items selected"
                  )}
                </span>
                {(min !== undefined || max !== undefined) && (
                  <span className="text-xs text-gray-500">{getConstraintText()}</span>
                )}
              </div>
              {registry.selectedCount > 0 && !readonly && (
                <button
                  onClick={() => registry.clearSelection()}
                  className="text-xs font-medium text-gray-500 transition-colors hover:text-gray-700"
                >
                  Clear all
                </button>
              )}
            </div>

            {registry.selectedCount > 0 && (
              <div className="flex max-h-[86px] flex-wrap gap-1.5 overflow-y-auto">
                {[...registry.selectedEntities].map(entity => (
                  <Badge
                    key={entity.id}
                    variant={BadgeVariant.GRAY}
                    size={BadgeSize.MD}
                    onRemove={!readonly ? () => registry.deselectEntity(entity) : undefined}
                    className="max-w-[200px] cursor-default"
                  >
                    <OverflowDisplayTooltip
                      content={`${entity.name}${getEntitySuffix(entity)}`}
                      styles={{ maxWidth: "100%" }}
                    >
                      {entity.name}
                      {getEntitySuffix(entity)}
                    </OverflowDisplayTooltip>
                  </Badge>
                ))}
              </div>
            )}
          </DialogDescription>
        </DialogHeader>

        <UnifiedRegistry currentFolderId={currentFolderId} onItemClick={handleItemClick} />

        <DialogFooter className="border-t border-gray-200 px-6 py-4">
          <div className="flex w-full justify-end gap-2">
            <Button variant="ghost" onClick={onCancel}>
              Cancel
            </Button>
            <Button onClick={handleConfirm} disabled={!canConfirm()}>
              Confirm
            </Button>
          </div>
        </DialogFooter>
      </>
    );
  },
);

export default ChooseEntityViewMulti;
